import React, { useContext, useEffect, useState } from "react";
import { Card, Row, Col, Form, Input, Button } from "antd";
import { AppStateContext } from "../context/AppContext";
import { AuthContext } from "../context/AuthContext";
import useRequest from "../hooks/useRequest";
import apiPath from "../constants/apiPath";
import { ShowToast, Severty } from "../helper/toast";

function ProfileSettings() {
  const { setPageHeading } = useContext(AppStateContext);
  const { userProfile, setUserProfile } = useContext(AuthContext);
  const [form] = Form.useForm();
  const { request } = useRequest();
  const [loading, setLoading] = useState(false);
  const [profileLoading, setProfileLoading] = useState(true);
  const [profile, setProfile] = useState(null);

  setPageHeading("Profile Settings");

  const fetchProfile = () => {
    setProfileLoading(true);
    request({
      url: apiPath.profile,
      method: "GET",
      onSuccess: (data) => {
        const res = data?.data || data;
        setProfile(res);
        form.setFieldsValue({
          name: res.name,
          mobileNumber: res.mobileNumber,
        });
        setProfileLoading(false);
      },
      onError: () => {
        setProfileLoading(false);
        ShowToast("Failed to load profile", Severty.ERROR);
      },
    });
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  const onUpdateProfile = (values) => {
    const { name, mobileNumber } = values;
    setLoading(true);
    request({
      url: apiPath.updateProfile,
      method: "POST",
      data: {
        name: name?.trim(),
        mobileNumber: mobileNumber?.trim() || "",
      },
      onSuccess: (data) => {
        setLoading(false);
        const updated = data?.data || data;
        ShowToast("Profile updated successfully", Severty.SUCCESS);
        if (updated) {
          setProfile(updated);
          localStorage.setItem("userProfile", JSON.stringify(updated));
          setUserProfile(updated);
        }
      },
      onError: (err) => {
        setLoading(false);
        ShowToast(err?.response?.data?.message || "Update failed", Severty.ERROR);
      },
    });
  };

  if (profileLoading) {
    return (
      <div className="page-inner-headding">
        <Card loading />
      </div>
    );
  }

  return (
    <div className="page-inner-headding">
      <Row gutter={[24, 24]}>
        <Col xs={24} sm={24} md={20} lg={14} xl={12}>
          <Card title="Profile Settings" className="profile-settings-card">
            {/* Read-only: Employee ID, Email, Location */}
            <Row gutter={[0, 16]} style={{ marginBottom: 24 }}>
              <Col span={24}>
                <div className="profile-readonly-field">
                  <span className="profile-readonly-label">Employee ID</span>
                  <span className="profile-readonly-value">
                    {profile?.suid || "—"}
                  </span>
                </div>
              </Col>
              <Col span={24}>
                <div className="profile-readonly-field">
                  <span className="profile-readonly-label">Email</span>
                  <span className="profile-readonly-value">
                    {profile?.email || "—"}
                  </span>
                </div>
              </Col>
              <Col span={24}>
                <div className="profile-readonly-field">
                  <span className="profile-readonly-label">Location</span>
                  <span className="profile-readonly-value">
                    {profile?.location || "—"}
                  </span>
                </div>
              </Col>
            </Row>

            <Form
              form={form}
              layout="vertical"
              onFinish={onUpdateProfile}
              initialValues={{
                name: profile?.name,
                mobileNumber: profile?.mobileNumber,
              }}
            >
              <Form.Item
                label="Name"
                name="name"
                rules={[
                  { required: true, message: "Please enter name" },
                  {
                    pattern: /^[a-zA-Z\s]*$/,
                    message: "Only letters and spaces allowed",
                  },
                ]}
              >
                <Input placeholder="Enter your name" />
              </Form.Item>

              <Form.Item
                label="Mobile Number"
                name="mobileNumber"
                rules={[
                  {
                    validator: (_, value) => {
                      const trimmed = value?.trim?.() ?? "";
                      if (!trimmed) return Promise.resolve();
                      const digits = trimmed.replace(/\D/g, "");
                      if (digits.length === 10 && /^[6-9]/.test(digits))
                        return Promise.resolve();
                      if (digits.length === 12 && digits.startsWith("91") && /^91[6-9]/.test(digits))
                        return Promise.resolve();
                      return Promise.reject(
                        new Error("Enter a valid 10-digit mobile number (e.g. 9876543210 or +91 9876543210)")
                      );
                    },
                  },
                ]}
              >
                <Input placeholder="Enter mobile number (e.g. 9876543210)" maxLength={14} />
              </Form.Item>

              <Form.Item style={{ marginBottom: 0, marginTop: 8 }}>
                <Button type="primary" htmlType="submit" loading={loading} block>
                  Update Profile
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>
      </Row>

      <style>{`
        .profile-settings-card .ant-card-head-title { font-weight: 600; }
        .profile-readonly-field {
          display: flex;
          flex-direction: column;
          gap: 4px;
          padding: 10px 12px;
          background: #fafafa;
          border-radius: 6px;
          border: 1px solid #f0f0f0;
        }
        .profile-readonly-label {
          font-size: 12px;
          color: #8c8c8c;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .profile-readonly-value {
          font-size: 14px;
          color: #262626;
        }
      `}</style>
    </div>
  );
}

export default ProfileSettings;
